import { memo, useCallback, useMemo } from 'react';
import classes from 'classnames';
import { AttentionSeeker } from 'react-awesome-reveal';
import CheckIcon from 'assets/icons/check-circle.svg?react';
import ErrorIcon from 'assets/icons/error-circle.svg?react';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import SelectEvent from 'hooks/context/EventCollector/events/SelectEvent';
import styles from './Select.module.scss';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export type Props = {
  className?: string;
  value?: string | number;
  name?: string;
  error?: string;
  isTouched?: boolean;
  placeholder?: string;
  dataTest?: string;
  options?: Array<{ name: string; value: string | number }>;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  errorClassName?: string;
};

function Select({
  className,
  value = '',
  name,
  error,
  isTouched,
  placeholder,
  dataTest = 'select',
  options,
  onChange,
  onBlur,
  disabled = false,
  eventCollector,
  autoFocus,
  errorClassName,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();

  const showError = isTouched && error;
  const showCheck = isTouched && !error;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e);

      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new SelectEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? e.target.value,
          description: eventCollector?.description ?? name ?? '',
        }),
      );
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      onChange,
      track,
    ],
  );

  const optionsParsed = useMemo(
    () =>
      options?.map(({ name: nameOption, value: valueOption }) => (
        <option key={value} value={valueOption}>
          {nameOption}
        </option>
      )),
    [],
  );

  return (
    <div className={classes(styles.container, className)}>
      <div
        className={classes(styles.field, showError && styles['field-error'])}
      >
        <select
          name={name}
          data-test={dataTest}
          value={value}
          onBlur={onBlur}
          onChange={handleChange}
          className={styles.select}
          disabled={disabled}
          autoFocus={autoFocus}
        >
          <option disabled />
          {optionsParsed}
        </select>

        <label
          className={classes(
            styles.label,
            value.toString().length && styles.float,
          )}
        >
          {placeholder}
        </label>

        <div className={styles.badges}>
          {(showError || showCheck) && <div className={styles.divider} />}
          {showError && (
            <AttentionSeeker effect="headShake">
              <ErrorIcon
                className={classes(styles.badge, styles['error-icon'])}
              />
            </AttentionSeeker>
          )}
          {showCheck && <CheckIcon className={styles.badge} />}
        </div>
      </div>

      {showError && (
        <div
          className={classes(
            styles['error-message'],
            showError && styles['error-active'],
          )}
        >
          <ErrorMessage
            dataTest={`${dataTest}-errorMessage`}
            errors={error}
            className={errorClassName}
          />
        </div>
      )}
    </div>
  );
}

const SelectMemo = memo(Select);

export default SelectMemo;
